import { useEffect } from "react";
import { ArrowUpDownIcon, ChevronDownIcon } from "lucide-react";

import { Row, Text } from "@/components";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import {
  defaultPipelineKanbanSortLabel,
  pipelineKanbanSortOptions,
} from "@/lib/constants";
import { cn } from "@/lib/utils";
import { JobPipelineSortState } from "@/types";

const defaultKanbanSortOption = pipelineKanbanSortOptions.find(
  (o) => o.label === defaultPipelineKanbanSortLabel,
);

export const JobPipelineKanbanSortBy = ({
  disabled,
  setSortState,
  sortState,
}: {
  disabled?: boolean;
  setSortState: (next: JobPipelineSortState) => void;
  sortState: JobPipelineSortState | null;
}) => {
  /* Handlers */
  const selectedOption = sortState
    ? pipelineKanbanSortOptions.find(
        (o) => o.key === sortState.key && o.direction === sortState.direction,
      )
    : undefined;
  const selectedLabel = selectedOption?.label ?? defaultPipelineKanbanSortLabel;

  // If table was sorted in a way not in this dropdown, go back to default
  useEffect(() => {
    if (!sortState || selectedOption || !defaultKanbanSortOption) return;
    setSortState({
      key: defaultKanbanSortOption.key,
      direction: defaultKanbanSortOption.direction,
    });
  }, [sortState, selectedOption, setSortState]);

  const handleSelect = (label: string) => {
    const next = pipelineKanbanSortOptions.find((o) => o.label === label);
    if (!next) return;
    setSortState({ key: next.key, direction: next.direction });
  };

  /* Render */
  const render = () => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <Button
          className="h-9 max-w-[280px] gap-2 rounded-full border-slate-200 bg-white px-3 text-xs font-medium text-slate-700 hover:bg-slate-50"
          size="sm"
          variant="outline"
        >
          <ArrowUpDownIcon className="h-3.5 w-3.5 shrink-0" />
          <Row className="min-w-0 items-center gap-1">
            <Text className="shrink-0 text-xs font-medium text-slate-700">
              Sort by:
            </Text>
            <Text className="truncate text-xs font-medium text-slate-900">
              {selectedLabel}
            </Text>
          </Row>
          <ChevronDownIcon className="h-3.5 w-3.5 shrink-0 text-slate-400" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-72">
        {pipelineKanbanSortOptions.map((option) => (
          <DropdownMenuItem
            key={option.label}
            className={cn(
              "gap-2",
              option.label === selectedLabel && "bg-slate-100 font-medium",
            )}
            onSelect={() => {
              handleSelect(option.label);
            }}
          >
            <Text className="truncate text-sm">{option.label}</Text>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return render();
};
